import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { api } from "@/features/utils/fetch-server";
import { useTranslation } from "@/features/utils/i18n";
import { PhoneCall } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface CallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CallDialog({ open, onOpenChange }: CallDialogProps) {
  const { t } = useTranslation("calls");
  const navigate = useNavigate();
  const clientId = useCurrentClient()?.client?.id || "";
  const [contactType, setContactType] = useState<"none" | "phone" | "email">(
    "none",
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateCall = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (contactType === "phone" && !phoneNumber.trim()) {
      toast.error(t("messages.phoneNumberRequired"));
      return;
    }
    if (contactType === "email" && !email.trim()) {
      toast.error(t("messages.emailRequired"));
      return;
    }
    if (contactType === "email" && !email.includes("@")) {
      toast.error(t("messages.emailInvalid"));
      return;
    }

    try {
      setIsCreating(true);

      // Build context based on contact type
      const context: { phone_number?: string; email?: string } = {};
      if (contactType === "phone") {
        context.phone_number = phoneNumber;
      } else if (contactType === "email") {
        context.email = email;
      }

      const session = await api.post<
        { id: string },
        { context: typeof context }
      >(`/api/sessions/v1/${clientId}/sessions/start`, { context });
      toast.success(t("messages.sessionStarted"));

      // Open the new session in the conversation pane. Absolute, not relative:
      // the dialog is opened from the list, which is mounted both at `/calls`
      // and at `/calls/session/:id`, where a relative path would nest.
      navigate(`/${clientId}/calls/session/${session.id}`);
      onOpenChange(false);

      // Reset form
      setContactType("none");
      setPhoneNumber("");
      setEmail("");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.sessionStartError"),
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <PhoneCall className="h-5 w-5" />
          {t("dialog.title")}
          <DialogDescription>{t("dialog.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleCreateCall} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="contactType">{t("dialog.contactType")}</Label>
            <Select
              value={contactType}
              onValueChange={(value: "none" | "phone" | "email") =>
                setContactType(value)
              }
            >
              <SelectTrigger id="contactType">
                <SelectValue
                  placeholder={t("dialog.placeholders.contactType")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t("dialog.contactTypes.none")}
                </SelectItem>
                <SelectItem value="phone">
                  {t("dialog.contactTypes.phone")}
                </SelectItem>
                <SelectItem value="email">
                  {t("dialog.contactTypes.email")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {contactType === "phone" && (
            <div className="space-y-2">
              <Label htmlFor="phoneInput">{t("dialog.phoneNumber")}</Label>
              <Input
                id="phoneInput"
                type="tel"
                placeholder={t("dialog.placeholders.phone")}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {contactType === "email" && (
            <div className="space-y-2">
              <Label htmlFor="emailInput">{t("dialog.emailAddress")}</Label>
              <Input
                id="emailInput"
                type="email"
                placeholder={t("dialog.placeholders.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              {t("dialog.cancel")}
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? t("dialog.starting") : t("dialog.startCall")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
